import type { PimsExportFormat } from "@hooks/use-pims-export-format";
import { stripNbsp } from "@src/utils";
import { getClipboardPayload } from "@src/utils/clipboard";
import {
  createLocalMarked,
  splitStoredMarkdown,
} from "@src/utils/markedConfig";
import { Table } from "@tiptap/extension-table";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import TableRow from "@tiptap/extension-table-row";
import { Markdown } from "@tiptap/markdown";
import type { AnyExtension, Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import type { RefObject } from "react";

const localMarked = createLocalMarked();

/**
 * Convert a plain-text string (single \n between paragraphs) into
 * TipTap-compatible HTML. Each line becomes a <p>; empty lines become
 * empty <p></p> paragraphs. Markdown syntax characters are preserved as
 * literal text (escaped).
 */
export function textToHtml(text: string): string {
  if (!text) return "";
  return stripNbsp(text)
    .split("\n")
    .map(
      (line: string) =>
        `<p>${line.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")}</p>`,
    )
    .join("");
}

/**
 * Convert a markdown string (stored with single \n between paragraphs)
 * into TipTap-compatible HTML, preserving blank lines as empty <p></p>
 * paragraphs.
 *
 * The standard markdown parser collapses multiple blank lines into one
 * paragraph break. This function splits at blank-line boundaries first,
 * converts each content segment via `marked`, then re-inserts empty
 * paragraphs for each blank line.
 */
export function markdownToHtmlPreservingBlanks(text: string): string {
  if (!text) return "";
  return splitStoredMarkdown(stripNbsp(text))
    .map((part: string | number) =>
      typeof part === "number"
        ? "<p></p>".repeat(part)
        : (localMarked.parse(part, { async: false }) as string),
    )
    .join("");
}

/**
 * Strip `&nbsp;` markers that getMarkdown() inserts for empty paragraphs.
 * These appear as `&nbsp;` alone on a line (between `\n\n` boundaries).
 */
export function stripMarkdownEmptyParagraphs(text: string): string {
  return text.replace(/^&nbsp;$/gm, "");
}

/**
 * Halve every run of 2+ newlines produced by getMarkdown(), converting
 * from markdown's \n\n paragraph breaks back to our single \n format.
 * Odd counts round up (e.g. 3 → 2) to preserve extra visual breaks.
 */
export function collapseMarkdownNewlines(text: string): string {
  return text.replace(/\n{2,}/g, (m) =>
    "\n".repeat(Math.ceil(m.length / 2)),
  );
}

/** Normalize raw getMarkdown()/serializer output into the stored single-\n format. */
export function normalizeEditorMarkdown(raw: string): string {
  return collapseMarkdownNewlines(
    stripNbsp(stripMarkdownEmptyParagraphs(raw)),
  );
}

/**
 * Markdown extension whose serializer leaves text nodes untouched.
 * @tiptap/markdown >=3.22 HTML-encodes & < > and backslash-escapes markdown
 * syntax in text nodes; stored notes and their other consumers expect raw text.
 */
export const RawTextMarkdown = Markdown.extend({
  onBeforeCreate(event) {
    this.parent?.(event);
    (
      this.storage.manager as unknown as {
        encodeTextForMarkdown: (text: string) => string;
      }
    ).encodeTextForMarkdown = (text) => text;
  },
});

/**
 * Serialize the ProseMirror document slice between `from` and `to` back to
 * markdown, applying the same post-processing used throughout the editor.
 * Falls back to plain text extraction if the markdown serializer is not
 * available (e.g. before the editor's `onCreate` fires).
 */
export function setClipboardFromSelection(
  view: Editor["view"],
  event: ClipboardEvent,
  editorRef: RefObject<Editor | null>,
  from: number,
  to: number,
  format: PimsExportFormat,
) {
  const text = serializeSelectionAsMarkdown(
    view,
    editorRef,
    from,
    to,
  );
  const { plain, html } = getClipboardPayload(text, format);
  event.clipboardData?.setData("text/plain", plain);
  event.clipboardData?.setData("text/html", html);
}

const PLAIN_TEXT_STARTER_KIT_CONFIG = {
  heading: false,
  bold: false,
  italic: false,
  underline: false,
  strike: false,
  code: false,
  codeBlock: false,
  blockquote: false,
  bulletList: false,
  orderedList: false,
  listItem: false,
  horizontalRule: false,
} as const;

/**
 * Returns the TipTap extensions array for the given mode, optionally
 * appending extra extensions (e.g. Placeholder, input rules) for editable use.
 */

export function buildEditorExtensions(
  plainText: boolean,
  extraExtensions: AnyExtension[] = [],
): AnyExtension[] {
  return [
    StarterKit.configure(
      plainText
        ? PLAIN_TEXT_STARTER_KIT_CONFIG
        : { heading: { levels: [1, 2, 3] } },
    ),
    ...(plainText
      ? []
      : [Table, TableRow, TableHeader, TableCell, RawTextMarkdown]),
    ...extraExtensions,
  ];
}

/**
 * Converts stored content to TipTap-compatible HTML for the given mode.
 * Plain text escapes markdown syntax; rich text renders it via marked.
 */
export function contentToEditorHtml(
  content: string,
  plainText: boolean,
): string {
  return plainText
    ? textToHtml(content)
    : markdownToHtmlPreservingBlanks(content);
}

export function serializeSelectionAsMarkdown(
  view: Editor["view"],
  editorRef: RefObject<Editor | null>,
  from: number,
  to: number,
): string {
  const slice = view.state.doc.slice(from, to);
  const tempDoc = view.state.schema.topNodeType.create(
    null,
    slice.content,
  );
  const raw =
    editorRef.current?.storage?.markdown?.manager?.serialize(
      tempDoc.toJSON(),
    ) ?? view.state.doc.textBetween(from, to, "\n");
  return normalizeEditorMarkdown(raw);
}
